import { useEffect, useRef, useState } from "react";
import { DataManager } from "@syncfusion/ej2-data";
import { reportService } from "../../../../../../Services/report.service";
import { vehicleService } from "../../../../../../Services/vehicle.service";
import { storage } from "../../../../../../Services/storage.service";
import { storageKeys } from "../../../../../../Constants/storage.key";
import { CustomAdaptor } from "../utils/CustomAdaptor";
import { buildPositionsUrl, formatDateForQuery } from "../utils/positionsQuery";

export const usePositionsData = ({
  vehicleId,
  selectedGroup,
  startDate,
  endDate,
  onlyEvents,
  useReportService,
  activeTab,
  pageSize,
}) => {
  const token = storage.get(storageKeys.access_and_token);
  const [dataGridPositions, setDataGridPositions] = useState(null);
  const [registersMap, setRegistersMap] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [gridPageSettings, setGridPageSettings] = useState({
    pageSize: pageSize ?? 10,
    currentPage: 1,
    totalRecordsCount: 0,
  });

  const hasRefreshedRef = useRef(false);
  const lastReceivedRef = useRef(null);

  const buildPayload = (overrides = {}) => ({
    vehicleId,
    groupId: selectedGroup?.value ?? 0,
    startDate: formatDateForQuery(startDate),
    endDate: formatDateForQuery(endDate),
    onlyEvents: onlyEvents ? "S" : "N",
    lastReceived: lastReceivedRef.current,
    ...overrides,
  });

  const buildDataManager = (pageNumber = 1, customPageSize) => {
    const url = buildPositionsUrl({
      ...buildPayload(),
      pageNumber,
      pageSize: customPageSize ?? gridPageSettings.pageSize,
    });

    if (!url || !token?.token) return null;

    return new DataManager({
      url,
      adaptor: new CustomAdaptor(),
      headers: [{ Authorization: `Bearer ${token.token}` }],
    });
  };

  const fetchPositions = async (pageNumber = 1, customPageSize) => {
    if (!vehicleId) return;
    setDataLoading(true);

    try {
      const pageSizeToUse = customPageSize ?? gridPageSettings.pageSize;
      const payload = buildPayload({ pageNumber, pageSize: pageSizeToUse });

      const result = useReportService
        ? await reportService.fetchVehiclesPositionsPaged(payload)
        : await vehicleService.fetchVehiclePositions(vehicleId, payload);

      if (result?.lastReceived) {
        lastReceivedRef.current = result.lastReceived;
      }

      const adapted = {
        result: result?.items || [],
        count: Number(result?.paging?.totalLines ?? result?.totalLines ?? 0),
      };

      setGridPageSettings((prev) => ({
        ...prev,
        currentPage: pageNumber,
        totalRecordsCount: adapted.count,
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchPositionsMap = async (pageNumber = 0, pageSize = 0) => {
    if (!vehicleId) return [];
    setDataLoading(true);

    try {
      const payload = buildPayload({ pageNumber, pageSize });

      const positions = useReportService
        ? await reportService.fetchVehiclesPositionsPaged(payload)
        : await vehicleService.fetchVehiclePositions(vehicleId, payload);

      if (positions?.lastReceived) {
        lastReceivedRef.current = positions.lastReceived;
      }

      const list = positions?.items ?? [];
      setRegistersMap(list);
      return list;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setDataLoading(false);
    }
  };

  const search = async () => {
    const dm = buildDataManager(1, 15);
    if (dm) setDataGridPositions(dm);
    await fetchPositions(1);
    await fetchPositionsMap();
  };

  const refresh = async () => {
    const dm = buildDataManager(1, 15);
    if (dm) setDataGridPositions(dm);
    await fetchPositionsMap();
  };

  // Inicializa DataManager quando entra no tab de posições
  useEffect(() => {
    if (activeTab !== "positions" || !vehicleId || !token?.token) return;

    const dm = buildDataManager();
    if (dm) setDataGridPositions(dm);

    // FIX: já busca o total de registros na carga inicial. Sem isso o
    // fallback "Não há dados" aparecia mesmo havendo dados, pois
    // totalRecordsCount só era preenchido ao clicar em "Buscar" (search()).
    fetchPositions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vehicleId]);

  // Reseta e atualiza mapa ao trocar de veículo ou ao ativar a aba.
  // FIX: inclui activeTab nas deps. O vehicleId é atualizado (dispatch redux)
  // antes do activeTab virar "positions" (que só muda ~400ms depois em
  // chooseDisplay); sem activeTab nas deps, o guard falhava na hora do
  // vehicleId mudar e o efeito nunca re-executava -> mapa ficava vazio
  // ("Não há dados") mesmo havendo posições.
  useEffect(() => {
    if (activeTab === "positions" && vehicleId) {
      fetchPositionsMap();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vehicleId]);

  return {
    dataGridPositions,
    setDataGridPositions,
    registersMap,
    setRegistersMap,
    dataLoading,
    gridPageSettings,
    setGridPageSettings,
    fetchPositions,
    fetchPositionsMap,
    search,
    refresh,
    hasRefreshedRef,
  };
};
